import { useState, useEffect } from 'react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { useAdminLocale } from '../hooks/useAdminLocale';

const locales = [
  { code: 'de' as const, flag: '\u{1F1E9}\u{1F1EA}', label: 'DE' },
  { code: 'en' as const, flag: '\u{1F1EC}\u{1F1E7}', label: 'EN' },
];

export function LocaleSelector() {
  const { contentLocale, setContentLocale } = useAdminLocale();

  return (
    <div className="flex items-center gap-1 bg-slate-700/50 rounded-lg p-1">
      {locales.map(({ code, flag, label }) => (
        <button
          key={code}
          onClick={() => setContentLocale(code)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
            contentLocale === code
              ? 'bg-teal-500 text-white shadow-sm'
              : 'text-slate-300 hover:bg-slate-600 hover:text-white'
          }`}
        >
          <span>{flag}</span>
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Toggle to enable/disable English locale for all users.
 * Controls the `is_active` flag on the 'en' row in `supported_locales`.
 */
export function LocalePublicToggle() {
  const [isEnActive, setIsEnActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getSupabaseAdminClient()
      .from('supported_locales')
      .select('is_active')
      .eq('code', 'en')
      .single()
      .then(({ data }: { data: { is_active: boolean } | null }) => {
        setIsEnActive(data?.is_active ?? false);
        setIsLoading(false);
      });
  }, []);

  const handleToggle = async () => {
    const newValue = !isEnActive;
    setIsSaving(true);
    try {
      const { error } = await getSupabaseAdminClient()
        .from('supported_locales')
        .update({ is_active: newValue, updated_at: new Date().toISOString() } as never)
        .eq('code', 'en');

      if (error) throw error;
      setIsEnActive(newValue);
    } catch (err) {
      console.error('Error toggling locale:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return null;

  return (
    <button
      onClick={handleToggle}
      disabled={isSaving}
      className="flex items-center gap-2 w-full text-left group"
      title={isEnActive ? 'Englisch ist für alle User aktiv' : 'Englisch ist nur für Admins sichtbar'}
    >
      <div
        className={`relative w-8 h-[18px] rounded-full transition-colors ${
          isEnActive ? 'bg-teal-500' : 'bg-slate-600'
        } ${isSaving ? 'opacity-50' : ''}`}
      >
        <div
          className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow transition-transform ${
            isEnActive ? 'translate-x-[14px]' : 'translate-x-0.5'
          }`}
        />
      </div>
      <span className={`text-xs ${isEnActive ? 'text-teal-400' : 'text-slate-500'}`}>
        {isEnActive ? 'EN öffentlich' : 'EN nur Admin'}
      </span>
    </button>
  );
}
